import Images from '../../../state/stores/Image/images';
import { AntDesign } from '@expo/vector-icons';
import { COLORS, ORANGE } from '../../../config/designSystem/Colors';
import { HUGE_MARGIN_VERTICAL_FN, REGULAR_MARGIN_VERTICAL_FN } from '../../../config/designSystem/Layout';
import { Image, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ParagraphLRegular, ParagraphMRegular, ParagraphXLRegular } from '../../../config/designSystem/typography';
import { PathCard } from '../../Cards/PathCard';
import { RadarData } from './ContainRadar';
import { SessionPresentationProps } from '../config';
import { goTo } from '../../../utils/links/goTo';
import { observer } from 'mobx-react';
import { useBreakpointStyles } from '../../../hooks/useBreakpointStyles';

export const PresentRadar = observer(({ onNext, onFinish, data, labels }: SessionPresentationProps<RadarData>) => {
  // Responsive styles for image and description container
  const responsiveStyles = useBreakpointStyles({
    contentContainer: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 30,
      paddingVertical: 20,
      md: {
        flexDirection: 'row',
        gap: 20,
        justifyContent: 'center',
      }
    },
    descriptionContainer: {
      flex: 1,
      sm: {
        flex: 0,
        width: '100%',
      }
    }
  });

  // Extract data from props (passed by Container)
  const {
    feature,
    valPercentage,
    recommendedPath,
    isAnonymous,
    showAssistanceButton,
  } = data || {};

  const goToAssistance = () => {
    goTo.assistance();
  };

  const goToPath = () => {
    if (recommendedPath?._id) {
      goTo.path(recommendedPath._id);
    }
  };

  const handleAssistancePress = () => {
    if (onNext) {
      // Call onNext with redirect callback for assistance
      onNext();
      goToAssistance();
    } else {
      goToAssistance();
    }
  };

  const handlePathPress = () => {
    if (onNext) {
      // Call onNext with redirect callback for path
      onNext();
      goToPath();
    } else {
      goToPath();
    }
  };

  return (
    <View style={{ justifyContent: 'center', flex: 1 }}>
      <ScrollView style={[styles.card]}>
        <View style={{
          marginHorizontal: 10,
          borderBottomColor: 'gray',
          borderBottomWidth: 1,
        }}>
          <View style={styles.topCard}>
            <ParagraphXLRegular align={true}>{feature?.outputSentence}</ParagraphXLRegular>

            <View style={{ marginTop: 20 }}>
              <ParagraphXLRegular color={feature?.color || undefined}>{valPercentage}%</ParagraphXLRegular>
            </View>
          </View>

          <View style={responsiveStyles.contentContainer}>
            {feature?.logo === 'NORMAL' ? (
              <Image
                style={[styles.imageIcon, { width: 49, height: 54 }]}
                source={Images.other.lightbulb}
              />
            ) : (
              <AntDesign name='warning' color={ORANGE} size={45} />
            )}

            <View style={responsiveStyles.descriptionContainer}>
              <ParagraphMRegular selectable={true}>
                {feature?.description}
              </ParagraphMRegular>
            </View>
          </View>

          {showAssistanceButton ? (
            <TouchableOpacity
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'center',
                backgroundColor: COLORS.brand[500],
                height: 60,
                padding: 10,
                borderRadius: 10,
                marginBottom: 10
              }}
              onPress={handleAssistancePress}
            >
              <ParagraphMRegular color='white'>{labels?.GO_TO_ASSISTANCE}</ParagraphMRegular>
            </TouchableOpacity>
          ) : null}
        </View>

        {!isAnonymous && recommendedPath && (
          <View style={{
            alignItems: 'center',
            marginVertical: HUGE_MARGIN_VERTICAL_FN(data?.height || 800)
          }}>
            <View style={{
              marginBottom: REGULAR_MARGIN_VERTICAL_FN(data?.height || 800)
            }}>
              <ParagraphLRegular>{labels?.YOUR_RECOMMENDED_PATH}</ParagraphLRegular>
            </View>
            <View>
              <PathCard
                id={recommendedPath._id}
                onPress={handlePathPress}
              />
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  imageIcon: {
    marginRight: 15,
  },
  topCard: {
    alignItems: 'center',
    borderBottomColor: 'lightgrey',
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  card: {
    margin: 0,
    padding: 0,
  },
});
